import { useEffect, useMemo } from 'react'

import { useSetState } from '@domain-group/react-hooks'

import autoSelectProductOptions, { autoSelectApiPricingOptions } from '@functions/autoSelectProductOptions'
import checkProductRequiredCampaignFields from '@functions/checkProductRequiredCampaignFields'
import filterPriceListItemsByPriceListAndProduct from '@functions/filterPriceListItemsByPriceListAndProduct'
import findCurrentAgencyPriceList from '@functions/findCurrentAgencyPriceList'
import selectPriceListItemOptionValue from '@functions/selectPriceListItemOptionValue'

import type {
  CampaignQuoteItemModel, CampaignQuoteTemplateItemModel, OrderItemModel, ProviderPriceListItemOptionModel,
} from '@models/types'
import type { NewOption } from '@functions/autoSelectProductOptions'

import useAgencyPrice from '@hooks/useAgencyPrice'
import useProviderPriceLists from '@hooks/useProviderPriceLists'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import defaultRequestOptions from '../defaultRequestOptions'

type DefaultState = {
  selectedProviderPriceListItemOptions?: NewOption[],
  selectedProviderPriceListItemId: number | undefined,
}

const defaultState: DefaultState = {
  selectedProviderPriceListItemOptions: undefined,
  selectedProviderPriceListItemId: undefined,
}

function useEditItemOptions(
  entity: CampaignQuoteItemModel | CampaignQuoteTemplateItemModel | OrderItemModel,
) {
  const {
    // @ts-expect-error TS2339
    campaign_quote_id,
    // @ts-expect-error TS2339
    campaign_quote_template_id,
    options: itemOptions,
    // @ts-expect-error TS2339
    order_id,
    provider_id: selectedProviderId,
    provider_price_list_item_id,
    publication_id: selectedPublicationId,
    service_id: selectedServiceId,
    service_product_id: selectedServiceProductId,
  } = entity

  const [state, setState] = useSetState(defaultState)
  const { selectedProviderPriceListItemId, selectedProviderPriceListItemOptions } = state

  // Initial Setup
  useEffect(() => {
    if (!selectedProviderPriceListItemId && provider_price_list_item_id){
      setState({ selectedProviderPriceListItemId: provider_price_list_item_id })
    }
  }, [provider_price_list_item_id])

  // Entities
  const entities = useSelector(reduxState => reduxState.entities)
  const {
    agencies,
    agencyProviders,
    campaignQuotes,
    campaignQuoteTemplates,
    campaigns,
    orders,
    providerPriceListItemOptionValues,
    providerPriceListItemOptions,
    providerPriceListItems,
    providerPriceLists,
    publications,
    serviceProducts,
    services,
  } = entities

  // Selected Entities
  const campaignQuoteTemplate = campaignQuoteTemplates[campaign_quote_template_id] || {}
  const campaignQuote = campaignQuotes[campaign_quote_id] || {}
  const order = orders[order_id] || {}

  const agencyId = order.agency_id || campaignQuote.agency_id || campaignQuoteTemplate.agency_id || 0
  const agency = agencies[agencyId] || {}
  const campaignId = order.campaign_id || campaignQuote.campaign_id || 0

  const selectedCampaign = campaignId && campaigns[campaignId] ? campaigns[campaignId] : {}
  const selectedPriceListItem = selectedProviderPriceListItemId
    && providerPriceListItems[selectedProviderPriceListItemId]
    ? providerPriceListItems[selectedProviderPriceListItemId]
    : {}
  const selectedService = selectedServiceId && services[selectedServiceId] ? services[selectedServiceId] : {}
  const selectedServiceProduct = selectedServiceProductId && serviceProducts[selectedServiceProductId]
    ? serviceProducts[selectedServiceProductId] : {}
  // @ts-expect-error TS2339
  const isPublication = selectedService?.options?.publication || false
  const publication = selectedPublicationId
  && publications[selectedPublicationId] ? publications[selectedPublicationId] : {}

  // Current Price List
  const currentPriceList = findCurrentAgencyPriceList({
    agencyId,
    agencyProviders,
    // @ts-expect-error TS2322
    providerId: selectedProviderId,
    providerPriceLists,
    // @ts-expect-error TS2739
    publication,
    // @ts-expect-error TS2322
    serviceId: selectedServiceId,
    // @ts-expect-error TS2339
    startDateString: selectedCampaign?.dates?.start?.date,
  })

  // Load Provider Pricelists
  const providerPriceListsPayload = useProviderPriceLists({
    filters: {
      // publication price lists arent scoped to agency at this stage
      // so remove the param
      agency_id: selectedPublicationId ? undefined : agencyId,
      provider_id: selectedProviderId,
      publication_id: selectedPublicationId,
      service_id: selectedServiceId,
      // @ts-expect-error TS2339
      start_date: selectedCampaign?.start_date,
    },
    performHttpRequests: (
      !!agencyId
      && !!selectedProviderId
      && (!isPublication || !!selectedPublicationId)
      && !!selectedServiceId
    ),
    requestOptions: defaultRequestOptions.providerPriceList,
  })

  const { loading: loadingProviderPriceLists } = providerPriceListsPayload

  const { missingFields: missingCampaignFields } = checkProductRequiredCampaignFields({
    // @ts-expect-error TS2740
    campaign: selectedCampaign,
    // @ts-expect-error TS2740
    serviceProduct: selectedServiceProduct,
  })

  // Load Agency Price
  const { currentPrice, loading: loadingAgencyPrice } = useAgencyPrice({
    agency,
    // @ts-expect-error TS2322
    campaign: selectedCampaign,
    performHttpRequests: !missingCampaignFields.length && selectedProviderPriceListItemOptions !== undefined,
    // @ts-expect-error TS2740
    providerPriceListItem: selectedPriceListItem,
    // @ts-expect-error TS2322
    selectedProviderPriceListItemOptions,
  })

  // Load API Pricing
  const { data: apiPricingItem, loading: loadingApiPricing } = useReduxAction(
    'providerPriceListItems',
    'buildProviderPriceListItemOptions',
    {
      agency_id: agency?.id,
      // @ts-expect-error TS2339
      campaign_id: selectedCampaign?.id,
      // @ts-expect-error TS2339
      entityId: selectedPriceListItem?.id,
    },
    // @ts-expect-error TS2339
    [selectedPriceListItem?.id, missingCampaignFields.length],
    {
      // @ts-expect-error TS2339
      dispatchAction: (action, requestOptions) => action(selectedPriceListItem.id, requestOptions),
      shouldClearResult: !selectedServiceProductId,
      // @ts-expect-error TS2339
      shouldPerformFn: () => selectedPriceListItem?.api_pricing && !missingCampaignFields.length,
      skipLoadedForKeysCheck: true,
    },
  )

  // Autoselect Product Options Only
  useEffect(() => {
    if (selectedProviderPriceListItemId && provider_price_list_item_id !== selectedProviderPriceListItemId){
      const autoSelectPayload = autoSelectProductOptions({
        // @ts-expect-error TS2322
        providerPriceListItem: selectedPriceListItem,
        providerPriceListItemOptionValues,
        providerPriceListItemOptions,
      })

      setState({
        selectedProviderPriceListItemOptions: autoSelectPayload.selectedProviderPriceListItemOptions,
      })
    }

    if (
      // @ts-expect-error TS2339
      !!selectedPriceListItem?.id
      && (provider_price_list_item_id === selectedProviderPriceListItemId)
      // @ts-expect-error TS2339
      && !selectedPriceListItem?.api_pricing
    ){
      setState({ selectedProviderPriceListItemOptions: itemOptions })
    }
  // @ts-expect-error TS2339
  }, [provider_price_list_item_id, selectedProviderPriceListItemId, selectedPriceListItem?.id])

  // Autoselect Options when using API Pricing
  useEffect(() => {
    if (
      // @ts-expect-error TS2339
      apiPricingItem?.api_pricing
      // @ts-expect-error TS2339
      && selectedPriceListItem?.id
      && !selectedProviderPriceListItemOptions?.length
    ){
      const autoSelectPayload = autoSelectApiPricingOptions({
        itemOptions,
        // @ts-expect-error TS2740
        providerPriceListItem: apiPricingItem,
        providerPriceListItemOptionValues,
        providerPriceListItemOptions,
      })

      setState({
        selectedProviderPriceListItemOptions: autoSelectPayload.selectedProviderPriceListItemOptions,
      })
    }
  // @ts-expect-error TS2339
  }, [apiPricingItem?.id, loadingApiPricing, selectedPriceListItem?.id])

  // Filter Price List Items
  const filteredProviderPriceListItems = useMemo(() => (
    filterPriceListItemsByPriceListAndProduct({
      providerPriceList: currentPriceList,
      providerPriceListItems,
      // @ts-expect-error TS2322
      serviceProduct: selectedServiceProduct,
    })
  // @ts-expect-error TS2339
  ), [currentPriceList.id, selectedServiceProduct.id])

  const updating = useSelector(reduxState => (
    reduxState.orderItems.updating
    || reduxState.campaignQuoteItems.updating
    || reduxState.campaignQuoteTemplateItems.updating
  ))

  // Valid Order
  // @ts-expect-error TS2339
  const validOrder = !!selectedServiceProduct.id
    && !missingCampaignFields.length
    // @ts-expect-error TS2339
    && !!selectedPriceListItem.id

  return {
    callbacks: {
      selectProviderPriceListItemOptionValue: (
        params: {
          providerPriceListItemOption: ProviderPriceListItemOptionModel,
          providerPriceListItemOptionValueId: number,
        },
      ) => {
        const { providerPriceListItemOption, providerPriceListItemOptionValueId } = params

        const selectPayload = selectPriceListItemOptionValue({
          providerPriceListItemOption,
          providerPriceListItemOptionValues,
          selectedProviderPriceListItemOptionValueId: providerPriceListItemOptionValueId,
          // @ts-expect-error TS2322
          selectedProviderPriceListItemOptions,
        })

        setState({
          selectedProviderPriceListItemOptions: selectPayload.selectedProviderPriceListItemOptions,
        })
      },
      selectProviderPriceListItemId: (id: number) => {
        const providerPriceListItem = providerPriceListItems[id]

        const autoSelectPayload = autoSelectProductOptions({
          providerPriceListItem,
          providerPriceListItemOptionValues,
          providerPriceListItemOptions,
        })

        setState({
          selectedProviderPriceListItemId: id,
          selectedProviderPriceListItemOptions: autoSelectPayload.selectedProviderPriceListItemOptions,
        })
      },
      setState,
    },
    currentPrice,
    filteredProviderPriceListItems,
    loadingAgencyPrice,
    loadingApiPricing,
    loadingProviderPriceLists,
    missingCampaignFields,
    selectedProviderPriceListItemId,
    selectedProviderPriceListItemOptions: selectedProviderPriceListItemOptions || [],
    selectedServiceProductId,
    updating,
    validOrder,
  }
}

export default useEditItemOptions
